import { ChangeEvent, createRef, useState } from "react";
import classnames from "classnames";
import { validate } from "email-validator";
import { NextPageContext } from "next";
import Error from "next/error";

import { getDispensary } from "api/requests/getDispensary";
import consumerApi from "api/services/consumer-api";
import { DISPENSARY_PAGES } from "constants/dispensary";
import { Action, Category, Label } from "constants/events";
import { Themes } from "custom-types/Themes";
import { getDomainCountryCode } from "redux/selectors/config";
import { AppStore } from "redux/store";
import logError from "utils/logError";
import { trackEvent } from "utils/trackEvent";
import { validateDispensary } from "utils/validateDispensary";

import Input from "components/botanic/Input";
import Select from "components/botanic/Select";
import DispensaryHead from "components/Dispensary/DispensaryHead";
import DispensaryHeaderSlim from "components/Dispensary/DispensaryHeaderSlim/DispensaryHeaderSlim";
import DispensaryTabs from "components/Dispensary/DispensaryTabs";
import DispensaryMap from "components/Dispensary/Map";
import Option from "components/Option";

import getRegionComplianceRules from "../../../api/requests/consumerApi/getRegionComplianceRules";
import { COMPLIANCE_RULESET_LOAD } from "../../../redux/reducers/complianceRules";

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
const AppointmentPage = ({ dispensary }: any) => {
  const form = createRef<HTMLFormElement>();
  const [
    disableButtonOnSuccessfulSubmission,
    setdisableButtonOnSuccessfulSubmission,
  ] = useState(false);
  const [emailFieldCompleted, setemailFieldCompleted] = useState(false);
  const [isEmailInvalid, setisEmailInvalid] = useState(false);
  const [isFirstNameInvalid, setisFirstNameInvalid] = useState(false);
  const [isLastNameInvalid, setisLastNameInvalid] = useState(false);
  const [isPhoneInvalid, setisPhoneInvalid] = useState(false);
  const [firstNameFieldCompleted, setFirstNameFieldCompleted] = useState(false);
  const [lastNameFieldCompleted, setLastNameFieldCompleted] = useState(false);
  const [phoneFieldCompleted, setphoneFieldCompleted] = useState(false);
  const [showSubmissionStatus, setshowSubmissionStatus] = useState(false);
  const [submissionIsSuccessful, setsubmissionIsSuccessful] = useState(false);
  const [timeFieldCompleted, settimeFieldCompleted] = useState(false);

  const validateEmail = (event: ChangeEvent<HTMLInputElement>) => {
    const patternMismatch = !validate(event.target.value);
    setisEmailInvalid(patternMismatch);
    setemailFieldCompleted(!patternMismatch);
  };

  const validatePhone = (event: ChangeEvent<HTMLInputElement>) => {
    const { patternMismatch } = event.target.validity;
    setisPhoneInvalid(patternMismatch);
    setphoneFieldCompleted(!patternMismatch);
  };

  const validateFirstName = (event: ChangeEvent<HTMLInputElement>) => {
    setdisableButtonOnSuccessfulSubmission(false);
    const { valueMissing } = event.target.validity;
    setisFirstNameInvalid(valueMissing);
    setFirstNameFieldCompleted(!valueMissing);
  };

  const validateLastName = (event: ChangeEvent<HTMLInputElement>) => {
    const { valueMissing } = event.target.validity;
    setisLastNameInvalid(valueMissing);
    setLastNameFieldCompleted(!valueMissing);
  };

  const handleSubmit = (event: ChangeEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.target);
    submitFormData(formData);
  };

  const onSuccessfulSubmission = () => {
    setemailFieldCompleted(false);
    setFirstNameFieldCompleted(false);
    setLastNameFieldCompleted(false);
    setphoneFieldCompleted(false);
    setshowSubmissionStatus(true);
    setsubmissionIsSuccessful(true);
  };

  if (!dispensary) {
    return <></>;
  }

  const submitFormData = (formData: FormData) => {
    setdisableButtonOnSuccessfulSubmission(true);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
    let appointmentTime: any;
    const formValues = {};
    for (const formField of formData) {
      // @ts-ignore (fix me please, do not replicate)
      if (formField === "schedule_for") {
        appointmentTime = formField[1];
      }
      // @ts-ignore (fix me please, do not replicate)
      formValues[formField[0]] = formField[1];
    }

    const { first_name, last_name, email, phone, schedule_for } =
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
      formValues as any;

    const fullName = `${first_name} ${last_name}`;

    consumerApi
      .post(
        `api/dispensaries/v1/${dispensary.id}/doctor_appointment_request`,
        {
          patient_email: email,
          patient_name: fullName,
          patient_phone: phone,
          schedule_for,
        },
        {
          headers: { "Content-Type": "application/json" },
        },
      )
      .then(() => {
        trackEvent(
          Category.doctorProfile,
          Action.click,
          Label.submitAppointment,
          {
            appointmentTime,
            dispensaryId: dispensary.id,
          },
        );

        onSuccessfulSubmission();
      })
      .catch((e) => {
        logError(e.message, {
          functionName: "appointment submitFormData",
          service: "consumer",
          statusCode: e.statusCode,
        });

        setshowSubmissionStatus(true);
        setsubmissionIsSuccessful(false);
      });
  };

  const getPrimaryLocation = () => {
    return (
      (dispensary.address1 ? dispensary.address1 : "") +
      (dispensary.address1 && dispensary.address2 ? " " : "") +
      (dispensary.address2 ? dispensary.address2 : "") +
      ((dispensary.address1 || dispensary.address2) && dispensary.city
        ? ", "
        : "") +
      (dispensary.city ? dispensary.city : "") +
      ((dispensary.address1 || dispensary.address2 || dispensary.city) &&
      dispensary.state
        ? ", "
        : "") +
      (dispensary.state ? dispensary.state : "")
    );
  };

  if (!dispensary) {
    return <Error statusCode={404} err="Profile could not be found" />;
  }

  const times = [
    "As soon as possible",
    "This Week",
    "Next Week",
    "This Month",
    "Next Month",
  ];

  const isSubmitDisabled = !(
    firstNameFieldCompleted &&
    lastNameFieldCompleted &&
    emailFieldCompleted &&
    phoneFieldCompleted &&
    timeFieldCompleted &&
    !disableButtonOnSuccessfulSubmission &&
    dispensary.email
  );

  return (
    <>
      <DispensaryHead dispensary={dispensary} pageType="doctor" />
      <DispensaryHeaderSlim dispensary={dispensary} />
      <DispensaryTabs
        activeTab={DISPENSARY_PAGES.appointment}
        dispensary={dispensary}
      />
      <div className="appointment-box flex flex-wrap justify-between container pt-xxl">
        <div className="form-box">
          {!submissionIsSuccessful && showSubmissionStatus && (
            <p className="text-left text-ocimene">
              There was a problem submitting. Try again.
            </p>
          )}
          {submissionIsSuccessful && showSubmissionStatus && (
            <div className="status-modal fixed inset-0 z-10">
              <span
                className="close-text text-leafly-white font-bold text-sm block"
                tabIndex={0}
                role="button"
                onClick={() => setshowSubmissionStatus(false)}
                onKeyPress={() => undefined}
              >
                Done
              </span>
              <div className="submission-status absolute right-0 left-0 bottom-0 bg-leafly-white p-lg">
                <div className="lg:text-center">
                  <p className="font-bold lg:text-md text-sm">Success!</p>
                  <p className="text-sm">{`${dispensary.name} will contact you to schedule`}</p>
                </div>
              </div>
            </div>
          )}
          <div className="appointment-availability">
            {!dispensary.email && (
              <p className="banner-message">
                Appointment request not available
              </p>
            )}
          </div>
          <form onSubmit={handleSubmit} ref={form} autoComplete="off">
            <div className="form-field my-md relative">
              <Input
                label="First name"
                id="first_name"
                name="first_name"
                onChange={validateFirstName}
                required
                theme={Themes.light}
              />
              {isFirstNameInvalid && (
                <p className="text-error text-xs">First name is required</p>
              )}
            </div>
            <div className="form-field my-xl relative">
              <Input
                label="Last name"
                id="last_name"
                name="last_name"
                onChange={validateLastName}
                required
                theme={Themes.light}
              />
              {isLastNameInvalid && (
                <p className="text-error text-xs">Last name is required</p>
              )}
            </div>
            <div
              className={classnames("form-field my-xl relative", {
                "text-error": isEmailInvalid,
              })}
            >
              <Input
                label="Email"
                id="_email"
                name="email"
                onChange={validateEmail}
                required
                theme={Themes.light}
              />
              {isEmailInvalid && (
                <p className="text-error text-xs">Valid email is required</p>
              )}
            </div>
            <div
              className={classnames("form-field my-xl relative", {
                "text-error": isPhoneInvalid,
              })}
            >
              <Input
                label="Phone number"
                type="tel"
                id="phone"
                name="phone"
                pattern="[0-9]{3}[0-9]{3}[0-9]{4}"
                onChange={validatePhone}
                required
                theme={Themes.light}
              />
              {isPhoneInvalid && (
                <p className="text-error text-xs">Valid phone is required</p>
              )}
            </div>
            <div className="form-field">
              <Select
                name="schedule_for"
                id="select-time"
                theme={Themes.light}
                defaultValue="Choose time"
                className="mb-xl"
                style={{
                  backgroundPosition: "calc(100% - 9px) 18px",
                  borderRadius: 0,
                  color: "var(--color-deep-green)",
                  fontSize: "var(--font-size-s)",
                  padding: "16px 8px",
                }}
                onChange={(event) => {
                  settimeFieldCompleted(event.target.value !== "Choose time");
                }}
              >
                <Option value="Choose time">Choose time</Option>
                {times.map((time) => {
                  return (
                    <Option value={time} key={time}>
                      {time}
                    </Option>
                  );
                })}
              </Select>
            </div>
            <SubmitButton disabled={isSubmitDisabled} />
          </form>
        </div>
        <div className="map-box">
          <div className="text-sm mb-md">{getPrimaryLocation()}</div>

          <DispensaryMap location={dispensary.primaryLocation} />
        </div>
      </div>

      <style jsx>{`
        .submission-status {
          position: fixed;
          bottom: 0;
        }
        .status-modal {
          background: rgba(0, 0, 0, 0.6);
        }
        input {
          height: 60px;
        }
        .terms-of-service {
          margin-top: 44px;
        }
        .map-box {
          height: 400px;
        }
        .map-box,
        .form-box {
          width: 45%;
        }
        .close-text {
          position: absolute;
          bottom: 13vh;
          right: 3vw;
        }
        .banner-message {
          height: 68px;
          font-size: 16px;
        }
        .banner-message span {
          font-weight: 300;
          float: right;
        }

        @media screen and (max-width: 768px) {
          .map-box {
            display: none;
          }
          .form-box {
            width: 100%;
          }
          .select {
            width: 100%;
          }
        }
      `}</style>
    </>
  );
};

AppointmentPage.getInitialProps = async (
  context: NextPageContext<AppStore>,
) => {
  const { req, res, store, query } = context;
  const { slug } = query;

  const countryCode = getDomainCountryCode(store.getState());

  const dispensary = await getDispensary(slug as string);

  const { redirect } = await validateDispensary({
    countryCode,
    dispensary,
    query,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- temporarily using non-null assertion before refactoring validateDispensary
    req: req!,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- temporarily using non-null assertion before refactoring validateDispensary
    res: res!,
  });

  if (redirect) {
    return {};
  }

  store.dispatch({
    dispensary,
    type: "dispensary/load",
  });

  try {
    const complianceRuleset = await getRegionComplianceRules(
      dispensary?.state,
      dispensary?.country,
    );

    if (complianceRuleset) {
      store.dispatch({
        complianceRuleset,
        type: COMPLIANCE_RULESET_LOAD,
      });
    }

    return {
      dispensary,
    };
  } catch (err) {
    logError(
      "Error processing data in /doctors/[slug]/appointement getInitialProps",
    );
  }

  return {
    dispensary,
  };
};

export default AppointmentPage;

function SubmitButton({ disabled }: { disabled: boolean }) {
  const submitClass = classnames(
    "font-bold rounded-none m-auto block text-deep-green py-md",
    {
      "bg-deep-green-20": disabled,
      "bg-yellow": !disabled,
    },
  );
  return (
    <input
      type="submit"
      value={disabled ? "complete form to submit" : "submit"}
      className={submitClass}
      style={{ height: "51px" }}
      disabled={disabled}
    />
  );
}
